import { cx } from '@vizit/theme';
import * as React from 'react';
import { Text, TextColor, TextVariant } from '../text';

interface ScoreDiffIndicatorProps extends React.HTMLAttributes<HTMLOutputElement> {
  /** The score difference. Positive renders an up arrow, negative renders a down arrow. */
  scoreDiff: number;
}

// Hand-rolled to match the Figma spec exactly (viewBox 10.6333, stroke 1.3 — ~12% ratio).
// ArrowCompactUp/DownIcon is ~18% and ArrowNarrowUp/DownIcon is ~6%, so neither matches.
const ARROW_UP_PATH = 'M5.31667 9.98333V0.65M9.98333 5.31667L5.31667 0.65L0.65 5.31667';
const ARROW_DOWN_PATH = 'M5.31667 0.65V9.98333M0.65 5.31667L5.31667 9.98333L9.98333 5.31667';

function DiffArrow({ direction, className }: { direction: 'up' | 'down'; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      width={9.33}
      height={9.33}
      viewBox="0 0 10.6333 10.6333"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d={direction === 'up' ? ARROW_UP_PATH : ARROW_DOWN_PATH}
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Displays a positive or negative score change with a directional arrow.
 * Returns `null` when `scoreDiff` is 0 — there's nothing to indicate.
 *
 * @example
 * <ScoreDiffIndicator scoreDiff={11} />
 */
const ScoreDiffIndicator = React.forwardRef<HTMLOutputElement, ScoreDiffIndicatorProps>((props, forwardedRef) => {
  const { scoreDiff, className, ...other } = props;

  if (scoreDiff === 0) {
    return null;
  }

  const isPositive = scoreDiff > 0;
  const absDiff = Math.abs(scoreDiff);

  return (
    <output
      ref={forwardedRef}
      aria-label={`Score ${isPositive ? 'increased' : 'decreased'} by ${absDiff}`}
      className={cx('flex items-center gap-3 px-1', className)}
      {...other}
    >
      <DiffArrow
        direction={isPositive ? 'up' : 'down'}
        className={isPositive ? 'text-success-700' : 'text-error-700'}
      />
      <Text variant={TextVariant.XsMedium} elementType="span" color={TextColor.Tertiary}>
        {absDiff}
      </Text>
    </output>
  );
});

ScoreDiffIndicator.displayName = 'ScoreDiffIndicator';

export { ScoreDiffIndicator };
export type { ScoreDiffIndicatorProps };
